import React, { useState } from "react";
import { Phone, Mail, MapPin, MessageCircle, Send } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import PublicLayout from "@/components/layout/PublicLayout";
import PageHeader from "@/components/PageHeader";
import api, { trackEvent, formatApiErrorDetail } from "@/lib/api";
import { useSite, waLink } from "@/context/SiteContext";

export default function Contact() {
  const { settings } = useSite();
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      trackEvent("callback_request", { subject: form.subject });
      toast.success("Message sent! We'll get back to you soon.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to send"); }
    finally { setLoading(false); }
  };

  const items = [
    { icon: Phone, label: "Phone", value: settings.phone, href: `tel:${settings.phone}`, ev: "phone_click" },
    { icon: Mail, label: "Email", value: settings.email, href: `mailto:${settings.email}`, ev: "email_click" },
    { icon: MapPin, label: "Address", value: settings.address },
  ];

  return (
    <PublicLayout>
      <Seo title="Contact Us" description="Get in touch with Dreamzz HR & Consultancy for recruitment, staffing and HR services across Karnataka." />
      <PageHeader eyebrow="Get In Touch" title="Contact Dreamzz HR" subtitle="Reach out for recruitment, staffing or HR consultancy — we're here to help." />
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-12 grid lg:grid-cols-[1fr_1.2fr] gap-10">
        <div className="space-y-4">
          {items.map(({ icon: Icon, label, value, href, ev }) => (
            <a key={label} href={href} onClick={() => ev && trackEvent(ev)} className="glass card-hover rounded-xl p-5 flex items-center gap-4 block">
              <span className="grid place-items-center h-12 w-12 rounded-xl bg-brand-emerald/15 text-brand-emerald"><Icon size={22} /></span>
              <div><div className="text-xs text-slate-400">{label}</div><div className="font-medium">{value}</div></div>
            </a>
          ))}
          <a href={waLink(settings.whatsapp, "Hi Dreamzz HR & Consultancy, I would like to enquire about your services.")} target="_blank" rel="noreferrer" onClick={() => trackEvent("whatsapp_click")}
            className="glass card-hover rounded-xl p-5 flex items-center gap-4 block">
            <span className="grid place-items-center h-12 w-12 rounded-xl bg-[#25D366]/15 text-[#25D366]"><MessageCircle size={22} /></span>
            <div><div className="text-xs text-slate-400">WhatsApp</div><div className="font-medium">Chat with us</div></div>
          </a>
        </div>
        <form onSubmit={submit} className="glass-strong rounded-2xl p-6 sm:p-8 grid gap-4 sm:grid-cols-2">
          <div><Label>Name *</Label><Input data-testid="contact-name" required value={form.name} onChange={upd("name")} className="bg-white/5 border-white/10 mt-1" /></div>
          <div><Label>Phone</Label><Input data-testid="contact-phone" value={form.phone} onChange={upd("phone")} className="bg-white/5 border-white/10 mt-1" /></div>
          <div className="sm:col-span-2"><Label>Email *</Label><Input data-testid="contact-email" type="email" required value={form.email} onChange={upd("email")} className="bg-white/5 border-white/10 mt-1" /></div>
          <div className="sm:col-span-2"><Label>Subject</Label><Input data-testid="contact-subject" value={form.subject} onChange={upd("subject")} className="bg-white/5 border-white/10 mt-1" /></div>
          <div className="sm:col-span-2"><Label>Message *</Label><Textarea data-testid="contact-message" required value={form.message} onChange={upd("message")} rows={4} className="bg-white/5 border-white/10 mt-1" /></div>
          <Button data-testid="contact-submit" type="submit" disabled={loading} className="sm:col-span-2 btn-press bg-brand-emerald hover:bg-[#C62828] text-white font-semibold h-12">
            <Send size={18} className="mr-2" />{loading ? "Sending..." : "Send Message"}
          </Button>
        </form>
      </section>
    </PublicLayout>
  );
}
